import React, { useState, useMemo } from 'react';
import { Box, Typography, TextField, Stack, Chip, Collapse } from '@mui/material';
import { CalendarMonth as CalendarIcon } from '@mui/icons-material';
import { format, add, differenceInMinutes } from 'date-fns';
import CardContent from './base/CardContent';
import {
  BaseAction,
  CardContentProps,
  ActionCommand,
  ActionCommandType,
  ScheduleAppointmentPayload,
} from '../../types';
import { useCardExpansion } from '../../store/hooks';
import AppointmentTypePicker from '../../../appointment-types/components/AppointmentTypePicker';
import { AppointmentType } from '../../../appointment-types/types';

// Durations in minutes for the dropdown
const durationOptions = [15, 30, 45, 60, 90, 120];

const CONTAINER_NAME = 'appointment-card';

interface AppointmentCardContentProps extends CardContentProps {
  action: BaseAction<'schedule_appointment'>;
}

const AppointmentCardContent: React.FC<AppointmentCardContentProps> = ({
  action: appointment,
  onCommand,
}) => {
  const [editedAppointment, setEditedAppointment] = useState<BaseAction<'schedule_appointment'>>({
    ...appointment,
  });
  const [isCustomDuration, setIsCustomDuration] = useState(false);
  const [endTime, setEndTime] = useState<string>(() => {
    // Initialize end time based on scheduled_for and scheduled_duration
    const startDate = new Date(appointment.action_payload.scheduled_for);
    const endDate = add(startDate, {
      minutes: appointment.action_payload.scheduled_duration,
    });
    return format(endDate, 'HH:mm');
  });
  const { isExpanded } = useCardExpansion();
  const expanded = isExpanded(appointment.action_id);

  // Helper to get just the time portion from a date string
  const getTimeFromDate = (dateString: string): string => {
    return format(new Date(dateString), 'HH:mm');
  };

  // Handle form input changes for nested payload properties
  const handlePayloadChange = (field: keyof typeof appointment.action_payload, value: any) => {
    setEditedAppointment(prev => ({
      ...prev,
      action_payload: {
        ...prev.action_payload,
        [field]: value,
      },
    }));
  };

  // Handle appointment type selection
  const handleAppointmentTypeChange = (appointmentType: AppointmentType | null) => {
    if (appointmentType) {
      handlePayloadChange('encounter_type_id', appointmentType.encounter_type_id);
      handlePayloadChange('encounter_type', appointmentType.encounter_type);

      // Update duration if appointment type has a default duration
      if (appointmentType.default_duration) {
        handleDurationSelect(appointmentType.default_duration);
      }
    } else {
      handlePayloadChange('encounter_type_id', undefined);
      handlePayloadChange('encounter_type', undefined);
    }
  };

  // Handle duration selection with chips
  const handleDurationSelect = (minutes: number) => {
    setIsCustomDuration(false);
    handlePayloadChange('scheduled_duration', minutes);

    // Update end time based on new duration
    try {
      const startDate = new Date(editedAppointment.action_payload.scheduled_for);
      const endDate = add(startDate, { minutes });
      setEndTime(format(endDate, 'HH:mm'));
    } catch (error) {
      console.error('Error calculating end time:', error);
    }
  };

  // Enable custom duration mode
  const handleCustomDuration = () => {
    setIsCustomDuration(true);

    // Calculate end time based on start time and current duration
    try {
      const startDate = new Date(editedAppointment.action_payload.scheduled_for);
      const endDateTime = add(startDate, {
        minutes: editedAppointment.action_payload.scheduled_duration,
      });
      setEndTime(format(endDateTime, 'HH:mm'));
    } catch (error) {
      console.error('Error calculating end time:', error);
    }
  };

  // Update scheduled date with new time
  const handleTimeChange = (timeString: string) => {
    try {
      const dateObj = new Date(editedAppointment.action_payload.scheduled_for);
      const [hours, minutes] = timeString.split(':').map(Number);
      dateObj.setHours(hours, minutes, 0, 0);

      handlePayloadChange('scheduled_for', dateObj.toISOString());
    } catch (error) {
      console.error('Error updating time:', error);
    }
  };

  // Format date for display
  const formatDate = (date: string) => {
    // Format as MM/DD/YYYY
    return format(new Date(date), 'M/d/yyyy');
  };

  // Format time without leading zeros and with AM/PM
  const formatTime = (dateString: string) => {
    try {
      const dateObj = new Date(dateString);
      if (isNaN(dateObj.getTime())) {
        return 'Invalid time';
      }
      return format(dateObj, 'h:mm a');
    } catch (error) {
      console.error('Error formatting time:', error);
      return 'Invalid time';
    }
  };

  const content = (
    <>
      {appointment.patient?.name && (
        <Typography variant="body2" color="text.secondary">
          {appointment.patient.name}
        </Typography>
      )}
      <Collapse in={!expanded} timeout="auto" unmountOnExit>
        {appointment.action_payload.encounter_type &&
          appointment.action_payload.encounter_type_id && (
            <Typography variant="body2" color="text.secondary">
              {appointment.action_payload.encounter_type}
            </Typography>
          )}
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            opacity: expanded ? 0 : 1,
            transition: 'opacity 0.3s',
            '> span': {
              whiteSpace: 'nowrap',
            },
            '> span:not(:last-child)': {
              '::after': {
                content: '", "',
                display: 'inline-block',
                width: 8,
              },
              [`@container ${CONTAINER_NAME} (max-width: 275px)`]: {
                display: 'block',
                '::after': {
                  content: 'none',
                },
              },
            },
          }}
        >
          <span>{formatDate(appointment.action_payload.scheduled_for)}</span>
          <span>{formatTime(appointment.action_payload.scheduled_for)}</span>
          <span>{appointment.action_payload.scheduled_duration} min</span>
        </Typography>
      </Collapse>
    </>
  );

  const selectedAppointmentType = useMemo(
    () =>
      editedAppointment.action_payload.encounter_type_id &&
      editedAppointment.action_payload.encounter_type
        ? ({
            encounter_type_id: editedAppointment.action_payload.encounter_type_id,
            encounter_type: editedAppointment.action_payload.encounter_type,
          } as AppointmentType)
        : null,
    [
      editedAppointment.action_payload.encounter_type_id,
      editedAppointment.action_payload.encounter_type,
    ],
  );

  // Create expandable content based on editing state
  const expandableContent = (
    <Stack spacing={3}>
      {/* Appointment Type Picker */}
      <AppointmentTypePicker
        value={selectedAppointmentType}
        onChange={handleAppointmentTypeChange}
        size="small"
      />

      {/* Date field */}
      <TextField
        label="Date"
        type="date"
        defaultValue={format(
          new Date(editedAppointment.action_payload.scheduled_for),
          'yyyy-MM-dd',
        )}
        onChange={e => {
          // Only update if we have a valid date string
          if (!e.target.value) return;

          const newDate = new Date(e.target.value);
          // Check if the date is valid
          if (isNaN(newDate.getTime())) return;

          const oldDate = new Date(editedAppointment.action_payload.scheduled_for);
          newDate.setHours(oldDate.getHours(), oldDate.getMinutes(), 0, 0);
          handlePayloadChange('scheduled_for', newDate.toISOString());
        }}
        InputLabelProps={{ shrink: true }}
        fullWidth
        size="small"
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: 1.5,
          },
        }}
      />

      {/* Time fields with custom styling to match mockup */}
      <Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Box sx={{ flex: 1 }}>
            <TextField
              label="Start Time"
              type="time"
              value={getTimeFromDate(editedAppointment.action_payload.scheduled_for)}
              onChange={e => handleTimeChange(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
            />
          </Box>

          {isCustomDuration && (
            <Box sx={{ flex: 1 }}>
              <TextField
                label="End Time"
                type="time"
                value={endTime}
                onChange={e => setEndTime(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1.5,
                  },
                }}
              />
            </Box>
          )}
        </Box>
      </Box>

      {/* Duration selection chips */}
      <Box sx={{ mt: 1 }}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            maxWidth: 400,
            mb: 0,
          }}
        >
          {durationOptions.map(option => (
            <Chip
              key={option}
              label={`${option} min`}
              onClick={() => handleDurationSelect(option)}
              variant={
                editedAppointment.action_payload.scheduled_duration === option && !isCustomDuration
                  ? 'filled'
                  : 'outlined'
              }
              color={
                editedAppointment.action_payload.scheduled_duration === option && !isCustomDuration
                  ? 'primary'
                  : 'default'
              }
              size="small"
            />
          ))}

          {/* Custom duration chip */}
          <Chip
            label="Custom"
            onClick={handleCustomDuration}
            variant={isCustomDuration ? 'filled' : 'outlined'}
            color={isCustomDuration ? 'primary' : 'default'}
            size="small"
          />
        </Box>
      </Box>
    </Stack>
  );

  return (
    <CardContent
      containerName={CONTAINER_NAME}
      id={appointment.action_id}
      title="Schedule Appointment?"
      content={content}
      avatarIcon={<CalendarIcon />}
      onCommand={(command: ActionCommand) => {
        if (command.type === ActionCommandType.ACCEPT) {
          if (expanded) {
            // Use ACCEPT_WITH_EDITS for edited appointments
            const updatedPayload = { ...editedAppointment.action_payload };

            // Handle custom duration calculation
            if (isCustomDuration) {
              try {
                const startDate = new Date(updatedPayload.scheduled_for);
                const [endHours, endMinutes] = endTime.split(':').map(Number);
                const endDate = new Date(startDate);
                endDate.setHours(endHours, endMinutes, 0, 0);

                if (endDate < startDate) {
                  endDate.setDate(endDate.getDate() + 1);
                }

                updatedPayload.scheduled_duration = differenceInMinutes(endDate, startDate);
              } catch (error) {
                console.error('Error calculating duration:', error);
              }
            }

            onCommand({
              type: ActionCommandType.ACCEPT_WITH_EDITS,
              payload: updatedPayload,
            });
          } else {
            onCommand({ type: ActionCommandType.ACCEPT });
          }
        } else {
          onCommand(command);
        }
      }}
      expandable={true}
      expandableContent={expandableContent}
      isExpandableContentEditable={true}
    />
  );
};

export default AppointmentCardContent;
